import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import type { CustomNode } from '../types';
import { buildNodeMetadata, metadataFileName } from '../utils/nodeMetadata';
import '../styles/custom-nodes.css';

type ViewMode = 'grid' | 'list';

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function CustomNodesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [nodes, setNodes] = useState<CustomNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'updated' | 'downloads'>('name');
  const [view, setView] = useState<ViewMode>(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem('cn-view') === 'list' ? 'list' : 'grid'
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<'bundles' | 'metadata' | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const canPublish =
    user?.role === 'admin' || (user?.featureFlags?.includes('custom-nodes-publish') ?? false);

  const loadNodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.customNodes();
      setNodes(Array.isArray(result) ? result : []);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadNodes();
  }, []);

  const setViewMode = (mode: ViewMode) => {
    setView(mode);
    try {
      localStorage.setItem('cn-view', mode);
    } catch {
      /* ignore */
    }
  };

  const categories = useMemo(() => {
    const set = new Set<string>();
    nodes.forEach((node) => {
      if (node.category) set.add(node.category);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [nodes]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matched = nodes.filter((node) => {
      if (category && node.category !== category) return false;
      if (!term) return true;
      return [node.name, node.bundleName, node.nodeName, node.category, node.description]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term));
    });
    return [...matched].sort((a, b) => {
      if (sortBy === 'downloads') return (b.downloadCount || 0) - (a.downloadCount || 0);
      if (sortBy === 'updated') return String(b.updatedAt).localeCompare(String(a.updatedAt));
      return a.name.localeCompare(b.name);
    });
  }, [nodes, search, category, sortBy]);

  const filteredIds = useMemo(() => filtered.map((n) => n.id), [filtered]);
  const allSelected = filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someSelected = filteredIds.some((id) => selected.has(id));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) filteredIds.forEach((id) => next.delete(id));
      else filteredIds.forEach((id) => next.add(id));
      return next;
    });

  const clearSelection = () => setSelected(new Set());

  const downloadOne = async (id: string) => {
    setDownloadingId(id);
    setError(null);
    try {
      const { blob, filename } = await api.downloadCustomNode(id);
      saveBlob(blob, filename);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadSelectedBundles = async () => {
    const chosen = nodes.filter((n) => selected.has(n.id));
    if (chosen.length === 0) return;
    setBulkBusy('bundles');
    setError(null);
    try {
      if (chosen.length === 1) {
        const { blob, filename } = await api.downloadCustomNode(chosen[0].id);
        saveBlob(blob, filename);
      } else {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (const node of chosen) {
          const { blob, filename } = await api.downloadCustomNode(node.id);
          zip.file(filename, blob);
        }
        const content = await zip.generateAsync({ type: 'blob' });
        saveBlob(content, 'custom-nodes.zip');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBulkBusy(null);
    }
  };

  const downloadSelectedMetadata = async () => {
    const chosen = nodes.filter((n) => selected.has(n.id));
    if (chosen.length === 0) return;
    setBulkBusy('metadata');
    setError(null);
    try {
      if (chosen.length === 1) {
        const node = chosen[0];
        const blob = new Blob([JSON.stringify(buildNodeMetadata(node), null, 2)], {
          type: 'application/json'
        });
        saveBlob(blob, metadataFileName(node));
      } else {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (const node of chosen) {
          zip.file(metadataFileName(node), JSON.stringify(buildNodeMetadata(node), null, 2));
        }
        const content = await zip.generateAsync({ type: 'blob' });
        saveBlob(content, 'custom-nodes-metadata.zip');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBulkBusy(null);
    }
  };

  return (
    <div className="custom-nodes-page">
      <div className="custom-nodes-page__header">
        <div>
          <h1>Custom Nodes</h1>
          <p className="hint">
            Share custom flow node bundles with colleagues. Each entry holds the deployable
            <code>.tgz</code>, its IO NodeMetadata, and instructions for configuring and deploying it.
          </p>
        </div>
        <div className="custom-nodes-page__actions">
          <button className="button button-secondary" onClick={() => navigate('/custom-nodes/whats-new')}>
            <span className="material-icons">history</span>
            What's New
          </button>
          {canPublish ? (
            <button className="button button-primary" onClick={() => navigate('/custom-nodes/new')}>
              <span className="material-icons">upload</span>
              Share a node
            </button>
          ) : null}
        </div>
      </div>

      <div className="custom-nodes-page__toolbar">
        <div className="custom-nodes-search">
          <span className="material-icons">search</span>
          <input
            type="text"
            placeholder="Search by name, bundle, category…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
        <select
          className="cn-select"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
          aria-label="Filter by category"
        >
          <option value="">All categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <select
          className="cn-select"
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value as 'name' | 'updated' | 'downloads')}
          aria-label="Sort nodes"
        >
          <option value="name">Name (A–Z)</option>
          <option value="updated">Recently updated</option>
          <option value="downloads">Most downloaded</option>
        </select>
        {(search || category) && (
          <button
            className="button button-tertiary button-small"
            onClick={() => {
              setSearch('');
              setCategory('');
            }}
          >
            Clear
          </button>
        )}
        <div className="cn-view-toggle" role="group" aria-label="View">
          <button
            type="button"
            className={view === 'grid' ? 'is-active' : ''}
            onClick={() => setViewMode('grid')}
            aria-label="Grid view"
          >
            <span className="material-icons">grid_view</span>
          </button>
          <button
            type="button"
            className={view === 'list' ? 'is-active' : ''}
            onClick={() => setViewMode('list')}
            aria-label="List view"
          >
            <span className="material-icons">view_list</span>
          </button>
        </div>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {loading ? <div className="loading">Loading custom nodes…</div> : null}

      {!loading && filtered.length > 0 ? (
        <div className="cn-selectbar">
          <label className="cn-check cn-check--label">
            <input ref={selectAllRef} type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
            <span>{selected.size > 0 ? `${selected.size} selected` : `Select all (${filtered.length})`}</span>
          </label>
          {selected.size > 0 ? (
            <div className="cn-selectbar__actions">
              <button className="button button-tertiary button-small" onClick={clearSelection}>
                Clear
              </button>
              <button
                className="button button-secondary button-small"
                onClick={downloadSelectedMetadata}
                disabled={bulkBusy !== null}
                title="Download the IO NodeMetadata for the selected nodes"
              >
                <span className="material-icons">data_object</span>
                {bulkBusy === 'metadata' ? 'Preparing…' : 'Metadata'}
              </button>
              <button
                className="button button-primary button-small"
                onClick={downloadSelectedBundles}
                disabled={bulkBusy !== null}
                title="Download the .tgz bundles for the selected nodes"
              >
                <span className="material-icons">download</span>
                {bulkBusy === 'bundles' ? 'Preparing…' : `Bundles (${selected.size})`}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {!loading && filtered.length === 0 ? (
        <div className="card card-subtle custom-nodes-empty">
          <span className="material-icons">widgets</span>
          <p>{nodes.length === 0 ? 'No custom nodes have been shared yet.' : 'No nodes match your filters.'}</p>
          {canPublish && nodes.length === 0 ? (
            <button className="button button-secondary" onClick={() => navigate('/custom-nodes/new')}>
              Share the first node
            </button>
          ) : null}
        </div>
      ) : null}

      {!loading && filtered.length > 0 && view === 'grid' ? (
        <div className="cn-grid">
          {filtered.map((node) => (
            <Link
              key={node.id}
              to={`/custom-nodes/${node.id}`}
              className={`cn-card${selected.has(node.id) ? ' cn-card--selected' : ''}`}
            >
              <label
                className="cn-card__check"
                onClick={(event) => event.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selected.has(node.id)}
                  onChange={() => toggleSelect(node.id)}
                />
              </label>
              <div className="cn-card__top">
                <div className="cn-card__icon">
                  {node.iconDataUrl ? (
                    <img src={node.iconDataUrl} alt="" />
                  ) : (
                    <span className="material-icons">extension</span>
                  )}
                </div>
                <span
                  className={`status ${node.visibility === 'public' ? 'status-approved' : 'status-pending'}`}
                >
                  {node.visibility === 'public' ? 'Everyone' : 'Teams'}
                </span>
              </div>
              <h3>{node.name}</h3>
              <p className="cn-card__bundle">
                {node.bundleName}
                <span className="cn-card__version">v{node.bundleVersion}</span>
              </p>
              {node.description ? <p className="cn-card__desc">{node.description}</p> : null}
              <div className="cn-card__footer">
                {node.category ? <span className="cn-chip">{node.category}</span> : <span />}
                <span className="cn-card__downloads">
                  <span className="material-icons">download</span>
                  {node.downloadCount}
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : null}

      {!loading && filtered.length > 0 && view === 'list' ? (
        <div className="cn-list">
          {filtered.map((node) => (
            <div
              key={node.id}
              className={`cn-list__row${selected.has(node.id) ? ' is-selected' : ''}`}
            >
              <label className="cn-check">
                <input
                  type="checkbox"
                  checked={selected.has(node.id)}
                  onChange={() => toggleSelect(node.id)}
                  aria-label={`Select ${node.name}`}
                />
              </label>
              <div className="cn-list__icon">
                {node.iconDataUrl ? (
                  <img src={node.iconDataUrl} alt="" />
                ) : (
                  <span className="material-icons">extension</span>
                )}
              </div>
              <Link to={`/custom-nodes/${node.id}`} className="cn-list__main">
                <span className="cn-list__name">{node.name}</span>
                <span className="cn-list__bundle">
                  {node.bundleName} <span className="cn-card__version">v{node.bundleVersion}</span>
                </span>
              </Link>
              {node.category ? <span className="cn-chip cn-list__cat">{node.category}</span> : <span className="cn-list__cat" />}
              <span
                className={`status ${node.visibility === 'public' ? 'status-approved' : 'status-pending'} cn-list__vis`}
              >
                {node.visibility === 'public' ? 'Everyone' : 'Teams'}
              </span>
              <span className="cn-list__dl">
                <span className="material-icons">download</span>
                {node.downloadCount}
              </span>
              <button
                type="button"
                className="cn-list__dlbtn"
                onClick={() => downloadOne(node.id)}
                disabled={downloadingId === node.id || !node.file}
                aria-label={`Download ${node.name}`}
                title="Download .tgz"
              >
                <span className="material-icons">{downloadingId === node.id ? 'hourglass_top' : 'download'}</span>
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
